import { Wordmark } from "../components/Wordmark";
import { Link } from "react-router-dom";

export function HowToPlay() {
  return (
    <div>
      <div className="topbar">
        <Wordmark size={20} />
        <Link to="/hide" className="btn btn-ghost" >
          Hide a place
        </Link>
      </div>

      <div className="page">
        <h1 style={{ fontSize: 34 }}>How to play</h1>

        <Section title="The board comes first">
          <p>
            Before anything else the hider names the board: the whole world, a
            continent, a country, a state, or a circle around somewhere you both
            know. The seeker is told what it is, for free, before they spend a
            point.
          </p>
          <p>
            This is the difference between a puzzle and a game. Nineteen
            thousand possible places is far past the point where knowing the
            hider helps, so the only move left is filtering a database. Say
            "somewhere in our city" and a read on the person becomes worth
            something.
          </p>
          <p>
            The board also sets how close counts. Twenty-five kilometres is fair
            across a planet and absurd across a county, so the win radius
            shrinks with the board — the seek screen tells you what it is.
          </p>
        </Section>

        <Section title="If you are hiding">
          <p>
            Name the board, pick one place on it, write a clue, send the link.
            You do not need to be online again.
          </p>
          <p>
            Your score depends on how much the seeker had to spend, not on
            whether they failed. It peaks when they find you having spent about
            80 of their 100 points. Somewhere unfindable scores 400. The obvious
            answer scores 150.
          </p>
        </Section>

        <Section title="If you are seeking">
          <p>
            You get <span className="num">100</span> points. Asking costs
            points, and so does looking: a guess is{" "}
            <span className="num">5</span>. Nothing else limits you, so the
            budget is the whole constraint.
          </p>
          <p>
            You are offered <span className="num">three</span> questions at a
            time rather than a price list of fourteen. Answering one changes
            which three you are offered next. A question whose answer the board
            already gives away is never offered at all.
          </p>
          <p>
            The price of a question is set by how much it is expected to narrow
            the field — nothing more. It is never priced from its own answer, so
            a cheap <em>What country are you in?</em> does not mean the country
            is large. Every answer reprices everything else.
          </p>
          <p>
            A guess comes back as a temperature and whether it beat your last
            one — warmer or colder. Not a distance: with guesses this cheap,
            three exact distances would let you solve for the spot instead of
            hunting for it. For direction rather than temperature, ask which way
            they are from where you just looked.
          </p>
          <p>
            The map shades where the answer can still be. Watch it after every
            answer: that shrinking region is the same number as the counter, in
            a form you can actually feel.
          </p>
        </Section>

        <Section title="Scoring">
          <p>
            <strong>Seeker:</strong> points left over × 10, scaled by how close
            you got. Full credit inside the win radius, 70% inside four times
            it, 40% inside ten times it. Your best look counts, even if a later
            one is worse — so hunting hard costs you budget, not credit.
          </p>
          <p>
            <strong>Hider:</strong> 1000 at a spend of 80, tapering either side,
            with a floor of 150. A round nobody solves is worth 400.
          </p>
        </Section>

        <Section title="What you can ask">
          <ul style={{ paddingLeft: 18, color: "var(--fog)" }}>
            <li>Are you north or south of the equator?</li>
            <li>Which continent are you on?</li>
            <li>What country are you in?</li>
            <li>What language do people speak around you?</li>
            <li>Are you anywhere near the sea?</li>
            <li>How many people live around you?</li>
            <li>What time is it where you are?</li>
            <li>What letter does the place start with?</li>
            <li>How high up are you?</li>
            <li>How far is the nearest big city?</li>
            <li>Which side of the road do they drive on?</li>
            <li>What does the land look like around you?</li>
            <li>Is there anything famous near you?</li>
            <li>Which way are you from where I just looked?</li>
          </ul>
          <p style={{ marginTop: 14 }}>
            They are not all the same kind of fact, and that is the point.
            Knowing the country tells you the language and the continent for
            free, so those get cheap. The first letter tells you nothing about
            where it is — which is exactly why it still pays after everything
            else.
          </p>
        </Section>

        <p style={{ marginTop: 34 }}>
          <Link className="btn btn-primary" to="/hide">
            Hide a place
          </Link>
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 30 }}>
      <h2 style={{ fontSize: 20, marginBottom: 8 }}>{title}</h2>
      <div style={{ color: "var(--fog)" }}>{children}</div>
    </section>
  );
}
